import { useEffect, useMemo, useRef, useState } from 'react';
import { randomUUID } from '@video-studio/shared';
import { api, ApiFoutException, type MediaDto, type Taak } from '../../api/client';
import type { useEditor } from '../../state/useEditor';

/**
 * AI-paneel: scène beschrijven, opties kiezen, kostenraming zien en een
 * generatie starten.
 *
 * De lijst met modellen en hun mogelijkheden komt van de server (zie
 * apps/api/src/ai/catalogus.ts). De UI belooft dus nooit een duur of resolutie
 * die de provider niet ondersteunt.
 */
export interface AiPaneelProps {
  editor: ReturnType<typeof useEditor>;
}

interface AiModelDto {
  id: string;
  label: string;
  beschrijving: string;
  beschikbaar: boolean;
  verouderd: boolean;
  ondersteuntReferentieAfbeelding: boolean;
  ondersteuntEersteFrame: boolean;
  opties: {
    beeldformaten: string[];
    resoluties: string[];
    duurSeconden: number[];
    personGeneration: string[];
  };
  prijsPerSecondeUsd: Record<string, number>;
  fotoToeslagUsd?: number;
}

interface AiCapaciteiten {
  provider: string;
  geconfigureerd: boolean;
  ontbrekendeSleutels: string[];
  modellen: AiModelDto[];
  budget: { perDagUsd: number; perMaandUsd: number; besteedVandaagUsd: number; besteedDezeMaandUsd: number };
  waarschuwing: string | null;
}

export function AiPaneel({ editor }: AiPaneelProps) {
  const [capaciteiten, setCapaciteiten] = useState<AiCapaciteiten | null>(null);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [modelId, setModelId] = useState<string>('');
  const [beeldformaat, setBeeldformaat] = useState('16:9');
  const [resolutie, setResolutie] = useState('720p');
  const [duur, setDuur] = useState(4);
  const [referentie, setReferentie] = useState<MediaDto | null>(null);
  const [fotoBezig, setFotoBezig] = useState(false);
  const fotoInvoer = useRef<HTMLInputElement>(null);
  const [bezig, setBezig] = useState<string | null>(null);
  const [taak, setTaak] = useState<Taak | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  const laad = async () => {
    setLaden(true);
    setFout(null);
    try {
      const resultaat = await fetch('/v1/ai/capaciteiten').then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new ApiFoutException(r.status, data?.fout?.code ?? 'AI', data?.fout?.melding ?? 'Onbekende fout');
        return data as AiCapaciteiten;
      });
      setCapaciteiten(resultaat);
      if (!modelId && resultaat.modellen[0]) setModelId(resultaat.modellen[0].id);
    } catch (f) {
      setFout(f instanceof Error ? f.message : 'De AI-mogelijkheden konden niet worden geladen.');
    } finally {
      setLaden(false);
    }
  };

  // Eenmalig laden bij het openen van het paneel.
  useMemo(() => {
    if (!capaciteiten && !laden) void laad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const model = capaciteiten?.modellen.find((m) => m.id === modelId) ?? capaciteiten?.modellen[0] ?? null;

  const taakLoopt = taak?.status === 'wacht' || taak?.status === 'bezig';
  const herlaadMedia = editor.herlaadMedia;
  const projectId = editor.project?.id;

  useEffect(() => {
    if (!projectId || projectId === 'leeg') return;
    let geannuleerd = false;
    void api.taken({ projectId, limiet: 20 }).then((resultaat) => {
      if (geannuleerd) return;
      const laatste = resultaat.items.find((item) => item.soort === 'ai-generatie');
      if (!laatste) return;
      setTaak((huidig) => {
        if (huidig) return huidig;
        if (laatste.status === 'wacht' || laatste.status === 'bezig') return laatste;
        return huidig;
      });
    }).catch(() => undefined);
    return () => {
      geannuleerd = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!taak || taak.id === 'aanvraag' || !taakLoopt) return;
    const id = window.setTimeout(async () => {
      try {
        const bijgewerkt = await api.taak(taak.id);
        setTaak(bijgewerkt);
        if (bijgewerkt.status === 'mislukt' || bijgewerkt.status === 'geannuleerd') {
          setFout(bijgewerkt.foutMelding ?? 'De generatie is mislukt.');
          setMelding(null);
        }
        if (bijgewerkt.status === 'klaar') {
          setFout(null);
          setMelding('De video is klaar en staat in de mediabibliotheek.');
          void herlaadMedia();
        }
      } catch {
        // De volgende ronde probeert de voortgang opnieuw.
      }
    }, 1200);
    return () => window.clearTimeout(id);
  }, [taak, taakLoopt, herlaadMedia]);

  useEffect(() => {
    statusRef.current?.scrollIntoView({ block: 'nearest' });
  }, [fout, melding, taak?.status, taak?.fase]);

  const raming = useMemo(() => {
    if (!model) return null;
    const prijs = model.prijsPerSecondeUsd[resolutie] ?? model.prijsPerSecondeUsd['720p'] ?? 0;
    const toeslag = referentie ? (model.fotoToeslagUsd ?? 0.01) : 0;
    return { prijsPerSeconde: prijs, toeslag, totaal: prijs * duur + toeslag };
  }, [model, resolutie, duur, referentie]);

  const uploadFoto = async (bestand: File) => {
    setFotoBezig(true);
    setFout(null);
    try {
      const resultaat = await api.uploadMedia(bestand, 'afbeelding');
      setReferentie(resultaat.asset);
      await editor.herlaadMedia();
    } catch (f) {
      setFout(f instanceof Error ? f.message : 'De foto kon niet worden geüpload.');
    } finally {
      setFotoBezig(false);
    }
  };

  const start = async () => {
    if (!prompt.trim()) {
      setFout('Beschrijf eerst de scène.');
      return;
    }
    if (!model) return;
    const bevestiging = window.confirm(
      `Weet je het zeker?\n\nDit start een betaalde generatie bij ${capaciteiten?.provider ?? 'de provider'}.\n` +
        `Model: ${model.label}\nDuur: ${duur} seconden\nResolutie: ${resolutie}\n` +
        (referentie ? `Startfoto: ${referentie.naam}\n` : '') +
        `Geraamde kosten: $${raming?.totaal.toFixed(2) ?? '?'}\n\n` +
        'Er wordt niet automatisch opnieuw geprobeerd als de status onduidelijk is.',
    );
    if (!bevestiging) return;

    setBezig('aanvraag');
    setFout(null);
    setMelding(null);
    setTaak({
      id: 'aanvraag',
      soort: 'ai-generatie',
      status: 'wacht',
      projectId: editor.project?.id ?? null,
      assetId: null,
      voortgang: 0.02,
      fase: 'Aanvraag versturen',
      pogingen: 0,
      foutCode: null,
      foutMelding: null,
      resultaat: null,
      gemaaktOp: new Date().toISOString(),
      gestartOp: null,
      afgerondOp: null,
    });
    try {
      const respons = await fetch('/v1/ai/generaties', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Unieke sleutel per poging: de server weigert een tweede betaalde
          // opdracht met dezelfde sleutel, ook bij dubbelklikken.
          'Idempotency-Key': randomUUID(),
        },
        body: JSON.stringify({
          projectId: editor.project?.id ?? null,
          prompt: prompt.trim(),
          model: model.id,
          beeldformaat,
          resolutie,
          duurSeconden: duur,
          referentieAssetId: referentie?.id ?? null,
        }),
      });
      const data = await respons.json();
      if (!respons.ok) {
        throw new ApiFoutException(respons.status, data?.fout?.code ?? 'AI', data?.fout?.melding ?? 'Aanvraag mislukt.');
      }
      if (data.mock) {
        setTaak(null);
        setMelding('De server staat in testmodus: er is geen betaalde generatie gestart.');
      } else if (typeof data.taakId === 'string') {
        try {
          setTaak(await api.taak(data.taakId));
        } catch {
          setTaak((huidig) =>
            huidig
              ? { ...huidig, id: data.taakId, fase: 'Gestart', voortgang: 0.05 }
              : huidig,
          );
        }
      }
    } catch (f) {
      setTaak(null);
      setFout(f instanceof Error ? f.message : 'De generatie kon niet worden gestart.');
    } finally {
      setBezig(null);
      void laad();
    }
  };

  if (!capaciteiten) {
    return (
      <div className="aipaneel">
        <h3>AI-video genereren</h3>
        {fout && <div className="melding melding--waarschuwing melding--klein">{fout}</div>}
        {laden ? <p className="paneel__hint">Mogelijkheden ophalen…</p> : (
          <button type="button" className="knop knop--breed" onClick={() => void laad()}>
            Opnieuw proberen
          </button>
        )}
      </div>
    );
  }

  const afbeeldingen = editor.media.filter((m) => m.soort === 'afbeelding');

  return (
    <div className="aipaneel">
      <h3>AI-video genereren</h3>

      <div ref={statusRef}>
      {taakLoopt && (
        <div className="voortgangsbalk" role="progressbar" aria-valuenow={Math.round((taak?.voortgang ?? 0) * 100)} aria-valuemin={0} aria-valuemax={100}>
          <div
            className="voortgangsbalk__vulling"
            style={{ width: `${Math.max(4, Math.round((taak?.voortgang ?? 0) * 100))}%` }}
          />
          <span>{taak?.fase ?? 'bezig'} — {Math.round((taak?.voortgang ?? 0) * 100)}%</span>
        </div>
      )}

      {fout && <div className="melding melding--fout melding--klein">{fout}</div>}
      {melding && <div className="melding melding--goed melding--klein">{melding}</div>}
      </div>

      {!capaciteiten.geconfigureerd && (
        <div className="melding melding--waarschuwing melding--klein">
          <strong>Nog niet ingesteld.</strong> De integratie is gebouwd, maar de serversleutel
          {' '}
          <code>{capaciteiten.ontbrekendeSleutels.join(', ') || 'GEMINI_API_KEY'}</code>
          {' '}
          ontbreekt. Zet die in het <code>.env</code>-bestand op de server. Genereren is daarom
          uitgeschakeld.
        </div>
      )}

      {capaciteiten.waarschuwing && (
        <div className="melding melding--info melding--klein">{capaciteiten.waarschuwing}</div>
      )}

      <label className="veld">
        <span>Scène beschrijven</span>
        <textarea
          rows={4}
          value={prompt}
          placeholder="Bijvoorbeeld: een warme woonkamer met een houten vloer, camera beweegt langzaam naar een radiator, ochtendlicht"
          onChange={(e) => setPrompt(e.target.value)}
        />
      </label>

      <label className="veld">
        <span>Model</span>
        <select value={model?.id ?? ''} onChange={(e) => setModelId(e.target.value)}>
          {capaciteiten.modellen.map((m) => (
            <option key={m.id} value={m.id} disabled={!m.beschikbaar}>
              {m.label}
              {m.verouderd ? ' (verouderd)' : ''}
              {!m.beschikbaar ? ' — niet beschikbaar' : ''}
            </option>
          ))}
        </select>
        {model && <span className="veld__uitleg">{model.beschrijving}</span>}
        {model?.verouderd && (
          <span className="veld__uitleg veld__uitleg--waarschuwing">
            Dit model wordt volgens de provider op korte termijn uitgezet.
          </span>
        )}
      </label>

      <label className="veld">
        <span>Beeldformaat</span>
        <select value={beeldformaat} onChange={(e) => setBeeldformaat(e.target.value)}>
          {(model?.opties.beeldformaten ?? ['16:9']).map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </label>

      <label className="veld">
        <span>Resolutie</span>
        <select value={resolutie} onChange={(e) => setResolutie(e.target.value)}>
          {(model?.opties.resoluties ?? ['720p']).map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>

      <label className="veld">
        <span>Duur</span>
        <select value={duur} onChange={(e) => setDuur(Number(e.target.value))}>
          {(model?.opties.duurSeconden ?? [4, 6, 8]).map((d) => (
            <option key={d} value={d}>
              {d} seconden
            </option>
          ))}
        </select>
        <span className="veld__uitleg">
          Alleen de lengtes die deze provider ondersteunt.
        </span>
      </label>

      <div className="startfoto">
        <span>Van foto naar video</span>
        <p className="veld__uitleg">
          Upload een foto of kies er een uit de bibliotheek. Die foto wordt het eerste beeld;
          Grok laat het daarna bewegen volgens de beschrijving. Zonder foto maakt Grok de scène
          alleen uit de tekst. Een foto kost ongeveer $
          {(model?.fotoToeslagUsd ?? 0.01).toFixed(2)} extra.
        </p>
        {referentie && (
          <figure className="startfoto__voorbeeld">
            <img src={referentie.bronUrl} alt="" />
            <figcaption>{referentie.naam}</figcaption>
          </figure>
        )}
        <div className="startfoto__knoppen">
          <button
            type="button"
            className="knop"
            disabled={fotoBezig || taakLoopt}
            onClick={() => fotoInvoer.current?.click()}
          >
            {fotoBezig ? 'Foto uploaden…' : 'Foto uploaden'}
          </button>
          {referentie && (
            <button type="button" className="knop knop--stil" onClick={() => setReferentie(null)}>
              Foto weghalen
            </button>
          )}
        </div>
        <input
          ref={fotoInvoer}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => {
            const bestand = e.target.files?.[0];
            e.target.value = '';
            if (bestand) void uploadFoto(bestand);
          }}
        />
        {afbeeldingen.length > 0 && (
          <label className="veld">
            <span>Of een foto uit de bibliotheek</span>
            <select
              value={referentie?.id ?? ''}
              onChange={(e) => setReferentie(afbeeldingen.find((a) => a.id === e.target.value) ?? null)}
            >
              <option value="">Geen foto</option>
              {afbeeldingen.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.naam}
                </option>
              ))}
            </select>
          </label>
        )}
        {model && !model.ondersteuntReferentieAfbeelding && referentie && (
          <span className="veld__uitleg veld__uitleg--waarschuwing">
            Dit model ondersteunt geen startfoto.
          </span>
        )}
      </div>

      <div className="kostenraming">
        <h4>Geraamde kosten</h4>
        <p className="kostenraming__bedrag">
          {raming ? `$${raming.totaal.toFixed(2)}` : '—'}
          {raming && (
            <span>
              {' '}
              ({raming.prijsPerSeconde.toFixed(2)} per seconde × {duur} s
              {raming.toeslag > 0 ? ` + $${raming.toeslag.toFixed(2)} voor de foto` : ''})
            </span>
          )}
        </p>
        <p className="kostenraming__detail">
          Dit is een schatting op basis van de officiële prijslijst. Werkelijke kosten worden pas
          geregistreerd als de provider ze terugmeldt en kunnen afwijken.
        </p>
        <p className="kostenraming__detail">
          Budget: ${capaciteiten.budget.besteedVandaagUsd.toFixed(2)} van $
          {capaciteiten.budget.perDagUsd.toFixed(2)} vandaag · $
          {capaciteiten.budget.besteedDezeMaandUsd.toFixed(2)} van $
          {capaciteiten.budget.perMaandUsd.toFixed(2)} deze maand
        </p>
      </div>

      <button
        type="button"
        className="knop knop--primair knop--breed"
        disabled={!capaciteiten.geconfigureerd || bezig !== null || taakLoopt || !model?.beschikbaar || !prompt.trim()}
        onClick={() => void start()}
        title={
          !capaciteiten.geconfigureerd
            ? 'Zet eerst de API-sleutel op de server'
            : 'Start een betaalde generatie'
        }
      >
        {bezig || taakLoopt
          ? 'Bezig…'
          : referentie
            ? 'Video van deze foto maken (betaald)'
            : 'Generatie starten (betaald)'}
      </button>

      <p className="paneel__hint">
        Er wordt nooit automatisch opnieuw geprobeerd als onduidelijk is of een aanvraag is
        uitgevoerd. Dat voorkomt dubbele kosten.
      </p>
    </div>
  );
}
